import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import type { AppDispatch } from "../../../../core/data/redux/store";
import type { Application } from "../../../../core/data/redux/recruitment/types";
import {
  selectApplications,
  selectApplicationsLoading,
  selectApplicationFilters,
  selectApplicationsPagination,
} from "../../../../core/data/redux/recruitment/selectors";
import { setApplicationFilters } from "../../../../core/data/redux/recruitment/slice";
import {
  fetchApplications,
  updateApplicationStatus,
  bulkUpdateApplicationStatus,
  exportApplications,
} from "../../../../core/data/redux/recruitment/thunks";
import { notify } from "../../../../services/toast.config";
import { all_routes } from "../../../router/all_routes";

import RecruitmentHeader from "../../components/RecruitmentHeader";
import ApplicationFilters from "../components/CandidateFilters";
import ApplicationTable from "../components/CandidateTable";
import StatusUpdateModal, {
  type StatusUpdatePayload,
} from "../components/StatusUpdateModal";
import ScheduleInterviewModal from "../components/ScheduleInterviewModal";
import Footer from "../../components/Footer";

interface ApplicationsManagementProps {
  viewMode?: "list" | "grid" | "kanban";
}

const ApplicationsManagement: React.FC<ApplicationsManagementProps> = ({
  viewMode = "list",
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const applications = useSelector(selectApplications);
  const loading = useSelector(selectApplicationsLoading);
  const filters = useSelector(selectApplicationFilters);
  const pagination = useSelector(selectApplicationsPagination);
  const activeCompany = useSelector(
    (state: any) => state.recruitment.activeCompany,
  );

  const totalCount =
    (pagination as any)?.total_applications ??
    (pagination as any)?.total ??
    applications.length;

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusModalApp, setStatusModalApp] = useState<Application | null>(
    null,
  );
  const [statusModalLoading, setStatusModalLoading] = useState(false);

  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<Application | null>(null);
  const [rejectModalLoading, setRejectModalLoading] = useState(false);

  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleTargetApp, setScheduleTargetApp] = useState<Application | null>(null);

  const toTitleCase = (str: string) =>
    str.charAt(0).toUpperCase() + str.slice(1);

  useEffect(() => {
    if (!activeCompany?.company_id) return;
    dispatch(
      fetchApplications({
        search: filters.search || undefined,
        job_id: filters.job_id || undefined,
        status: filters.status || undefined,
        source: filters.source || undefined,
        priority: filters.priority || undefined,
        min_score: filters.min_score ?? undefined,
        max_score: filters.max_score ?? undefined,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
        sort_by: filters.sort_by || undefined,
        sort_order: filters.sort_order || undefined,
        page: filters.page,
        limit: filters.limit,
      }),
    );
  }, [
    dispatch,
    filters,
    activeCompany?.company_id,
    activeCompany?.company_type,
  ]);

  const handleFilterChange = useCallback(
    (partial: Record<string, any>) => dispatch(setApplicationFilters(partial)),
    [dispatch],
  );

  const handleViewDetails = useCallback(
    (applicationId: number) => {
      navigate(
        all_routes.candidateDetails.replace(":id", String(applicationId)),
      );
    },
    [navigate],
  );

  const openStatusModal = useCallback(
    (app: Application, defaultStatus?: string) => {
      setStatusModalApp({
        ...app,
        __defaultStatus: defaultStatus,
      } as any);
      setStatusModalOpen(true);
    },
    [],
  );

  const closeStatusModal = useCallback(() => {
    setStatusModalOpen(false);
    setStatusModalApp(null);
  }, []);

  const openRejectSingle = useCallback((app: Application) => {
    setRejectTarget(app);
    setRejectModalOpen(true);
  }, []);

  const openRejectBulk = useCallback(() => {
    setRejectTarget(null);
    setRejectModalOpen(true);
  }, []);

  const openScheduleModal = useCallback((app: Application) => {
    setScheduleTargetApp(app);
    setScheduleModalOpen(true);
  }, []);

  const handleStatusSubmit = useCallback(
    async (payload: StatusUpdatePayload) => {
      setStatusModalLoading(true);
      try {
        await dispatch(
          updateApplicationStatus({
            applicationId: payload.applicationId,
            newStatus: payload.newStatus,
            notes: payload.notes,
            candidateMessage: payload.candidateMessage,
            sendEmailNotification: payload.sendEmailNotification,
          }),
        ).unwrap();
        notify.success(
          `Status updated to ${payload.newStatus.replace(/_/g, " ")}`,
        );
        closeStatusModal();
        dispatch(
          fetchApplications({ page: filters.page, limit: filters.limit }),
        );
      } catch (err: any) {
        notify.error(
          err || err?.data?.message || err?.message || "Status update failed",
        );
      } finally {
        setStatusModalLoading(false);
      }
    },
    [dispatch, filters, closeStatusModal],
  );

  const handleBulkMoveStage = useCallback(
    async (targetStatus: string) => {
      try {
        await dispatch(
          bulkUpdateApplicationStatus({
            applicationIds: selectedRowKeys.map(Number),
            newStatus: targetStatus,
          }),
        ).unwrap();
        notify.success(
          `${selectedRowKeys.length} candidate(s) moved to ${targetStatus.replace(/_/g, " ")}`,
        );
        setSelectedRowKeys([]);
        dispatch(
          fetchApplications({ page: filters.page, limit: filters.limit }),
        );
      } catch (err: any) {
        notify.error(
          err || err?.data?.message || err?.message || "Bulk update failed",
        );
      }
    },
    [dispatch, selectedRowKeys, filters],
  );

  const handleExport = useCallback(async () => {
    notify.info("Exporting applications...");
    try {
      const blob = await dispatch(
        exportApplications({
          status: filters.status,
          source: filters.source,
          job_id: filters.job_id,
          priority: filters.priority,
        }),
      ).unwrap();
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.download = "applications_export.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      notify.success("Export downloaded");
    } catch {
      notify.error("Export failed");
    }
  }, [dispatch, filters]);

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <RecruitmentHeader title="Applications" breadcrumb="Applications">
            <button
              className="btn btn-outline-primary d-flex align-items-center mb-2"
              onClick={handleExport}>
              <i className="ti ti-file-export me-2" />
              Export
            </button>
          </RecruitmentHeader>

          <ApplicationFilters
            filters={filters}
            onFilterChange={handleFilterChange}
          />

          {viewMode === "list" ? (
            <ApplicationTable
              applications={applications}
              loading={loading}
              filters={filters}
              totalCount={totalCount}
              selectedRowKeys={selectedRowKeys}
              onFilterChange={handleFilterChange}
              onSelectionChange={setSelectedRowKeys}
              onViewDetails={handleViewDetails}
              onUpdateStatus={openStatusModal}
              onBulkMoveStage={handleBulkMoveStage}
              onBulkReject={openRejectBulk}
              onExport={handleExport}
              onScheduleInterview={openScheduleModal}
            />
          ) : (
            <div className="card border-0 shadow-sm rounded-4 p-5 text-center">
              <div className="mb-4">
                <i
                  className={`ti ti-layout-${viewMode} text-primary opacity-25`}
                  style={{ fontSize: 80 }}
                />
              </div>
              <h4 className="fw-bold text-dark mb-2">
                {toTitleCase(viewMode)} View Coming Soon
              </h4>
              <p className="text-muted mx-auto" style={{ maxWidth: 400 }}>
                We are currently refactoring our {viewMode} presentation to
                provide a more immersive experience for managing your candidate
                pipeline.
              </p>
              <div className="mt-4">
                <button
                  className="btn btn-primary rounded-pill px-4"
                  onClick={() => navigate(all_routes.applicationsList)}>
                  <i className="ti ti-arrow-left me-2" />
                  Back to List View
                </button>
              </div>
            </div>
          )}
        </div>

        <Footer />
      </div>

      <StatusUpdateModal
        open={statusModalOpen}
        application={statusModalApp}
        loading={statusModalLoading}
        onCancel={closeStatusModal}
        onSubmit={handleStatusSubmit}
      />

      {scheduleTargetApp && (
        <ScheduleInterviewModal
          applicationId={scheduleTargetApp?.id || null}
          jobId={scheduleTargetApp?.job?.id || (scheduleTargetApp?.job as any)?.job_id || null}
          candidatePhone={scheduleTargetApp?.candidate?.phone || ""}
          open={scheduleModalOpen}
          onClose={() => setScheduleModalOpen(false)}
          onSuccess={() => {
            setScheduleModalOpen(false);
            dispatch(fetchApplications({ page: filters.page, limit: filters.limit }));
            notify.success("Interview scheduled successfully!");
          }}
        />
      )}
    </>
  );
};

export default ApplicationsManagement;
